// Cross-checks enum-backed ingestion values against the same option sources the
// submission form's own selectors read.
//
// Values reaching the carry-forward path come from three places — AI extraction, web
// verification, and manual edits in the review dialog — and only the first ever
// resembled what program state expects. A NAICS display string ("482111 – Line-Haul
// Railroads"), a free-text industry class ("Rail Transportation"), or a code the
// pricing service has never heard of all used to be written straight through, leaving
// the selector with nothing to render and the submission with an unusable value.
// Resolving here means an unmatched value is reported to the reviewer instead.
//
// Option sources, matching the selectors:
//   naics / category_code → pricing search APIs (config.NAICS_SEEKER, CATEGORY_CODE_SEEKER)
//   industryClass         → state.temp.exposureMetrics.industryClassChoices ({key, name})
//   currency              → state.temp.staticData.currencyCodesList (code strings)

export const ENUM_RESOLVED = "resolved";
export const ENUM_AMBIGUOUS = "ambiguous";
export const ENUM_UNMATCHED = "unmatched";
export const ENUM_UNCHECKED = "unchecked";
export const ENUM_EMPTY = "empty";

const MAX_CANDIDATES = 5;

// Keyed by backend ingestion field key.
export const ENUM_FIELDS = {
  naics: { programKey: "naics", kind: "code", endpoint: "NAICSCodes" },
  categoryCode: {
    programKey: "categoryCode",
    kind: "code",
    endpoint: "GICSCategoryCodes",
  },
  industryClass: { programKey: "industryClass", kind: "choice" },
  currency: { programKey: "currency", kind: "currency" },
};

export const isEnumBackedField = (fieldKey) =>
  Object.prototype.hasOwnProperty.call(ENUM_FIELDS, fieldKey);

// ─── Parsing ──────────────────────────────────────────────────────────────────

// Every display form a code/title value can arrive in. NAICS and GICS codes are
// numeric, so a leading digit run is what distinguishes a coded value from free text.
const CODE_TITLE_PATTERNS = [
  /^\((\d[\d.]*)\)\s*(.*)$/, // "(482111) Line-Haul Railroads" — naicsRenderValue
  /^(\d[\d.]*)\s*[–—-]\s*(.*)$/, // "482111 – Line-Haul Railroads" — formatValue / web_value
  /^(\d[\d.]*)\s*:\s*(.*)$/, // "482111: Line-Haul Railroads"
];

export const parseCodeTitle = (raw) => {
  if (raw == null) return { code: null, title: null };

  if (typeof raw === "object" && !Array.isArray(raw)) {
    const code = raw.code == null ? null : String(raw.code).trim() || null;
    const title = raw.title == null ? null : String(raw.title).trim() || null;
    return { code, title };
  }

  const text = String(raw).trim();
  if (!text) return { code: null, title: null };

  for (const pattern of CODE_TITLE_PATTERNS) {
    const match = text.match(pattern);
    if (match) return { code: match[1], title: match[2].trim() || null };
  }
  if (/^\d[\d.]*$/.test(text)) return { code: text, title: null };
  return { code: null, title: text };
};

// Codes cross provider boundaries as either strings or numbers.
const sameCode = (a, b) =>
  a != null && b != null && String(a).trim() === String(b).trim();

const formatCodeTitle = (code, title) => {
  if (code != null && title) return `${code} – ${title}`;
  if (code != null) return String(code);
  return title || "";
};

// Callers preserve the original reference when the canonical value says the same
// thing, so the usage tracker's strict-equality diff keeps reading "unchanged".
export const sameEnumValue = (a, b) => {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a === "object" || typeof b === "object") {
    const pa = parseCodeTitle(a);
    const pb = parseCodeTitle(b);
    return sameCode(pa.code, pb.code) && (pa.title ?? "") === (pb.title ?? "");
  }
  return String(a).trim() === String(b).trim();
};

// ─── Resolvers ────────────────────────────────────────────────────────────────

// Returns [] for "no matches" and null for "could not check", which callers must
// distinguish: the first is bad data, the second is a network problem.
const searchOptions = async (endpoint, query, ctx) => {
  const build = ctx?.claimsApi?.endpoints?.[endpoint];
  if (!ctx?.dispatch || !build?.initiate) return null;
  try {
    const result = await ctx.dispatch(build.initiate({ query: String(query) }));
    // claimsService's baseQuery catches transport failures and resolves with
    // {error} instead of throwing, so an outage would otherwise read as "no such
    // option" and get the reviewer's value discarded. Only a genuine array counts
    // as an answer; anything else means we could not check.
    if (result?.error || !Array.isArray(result?.data)) return null;
    return result.data;
  } catch {
    return null;
  }
};

const toCodeOption = (option) => ({
  value: { code: option._CODE, title: option._TITLE },
  label: formatCodeTitle(option._CODE, option._TITLE),
});

const resolveCodeField = async (config, raw, ctx) => {
  const { code, title } = parseCodeTitle(raw);
  const label = formatCodeTitle(code, title);
  if (code == null && !title) return { status: ENUM_EMPTY, label: "" };

  let unreachable = false;

  // The code is the authoritative half — confirm it exists before trusting the
  // title, so a plausible-sounding title can't smuggle a nonexistent code through.
  if (code != null) {
    const byCode = await searchOptions(config.endpoint, code, ctx);
    if (byCode === null) unreachable = true;
    else {
      const exact = byCode.find((option) => sameCode(option._CODE, code));
      if (exact) return { status: ENUM_RESOLVED, ...toCodeOption(exact) };
    }
  }

  // No such code. Offer what the title actually maps to rather than writing the
  // proposed code through unchecked.
  const byTitle = title ? await searchOptions(config.endpoint, title, ctx) : [];
  if (byTitle === null) unreachable = true;

  if (unreachable) return { status: ENUM_UNCHECKED, value: raw, label };

  const candidates = (byTitle || []).slice(0, MAX_CANDIDATES).map(toCodeOption);
  if (candidates.length) {
    // A title-only value with a single hit is unambiguous — nothing for the
    // reviewer to choose between.
    if (code == null && candidates.length === 1) {
      return { status: ENUM_RESOLVED, ...candidates[0] };
    }
    return { status: ENUM_AMBIGUOUS, label, candidates };
  }
  return { status: ENUM_UNMATCHED, label };
};

const nameTokens = (text) =>
  String(text)
    .toLowerCase()
    .split(/[^a-z0-9&]+/)
    .filter((token) => token.length > 2);

const shareAToken = (name, text) => {
  const known = new Set(nameTokens(name));
  return nameTokens(text).some((token) => known.has(token));
};

// The industry-class list lives in redux behind a fire-and-forget thunk, so a page
// that never mounted a class selector has an empty list. Fall back to fetching it
// rather than skipping the check.
const loadChoices = async (ctx) => {
  const preloaded = ctx?.industryClassChoices;
  if (Array.isArray(preloaded) && preloaded.length > 0) return preloaded;

  const build = ctx?.claimsApi?.endpoints?.industryClasses;
  if (!ctx?.dispatch || !build?.initiate) return null;
  try {
    const result = await ctx.dispatch(build.initiate());
    return Array.isArray(result?.data) && result.data.length > 0
      ? result.data
      : null;
  } catch {
    return null;
  }
};

const resolveChoiceField = async (raw, ctx) => {
  const text = String(raw ?? "").trim();
  if (!text) return { status: ENUM_EMPTY, label: "" };

  const choices = await loadChoices(ctx);
  if (!Array.isArray(choices) || choices.length === 0) {
    return { status: ENUM_UNCHECKED, value: raw, label: text };
  }

  const lower = text.toLowerCase();
  // Accept either half: triage can carry a stored key, the AI and the web verifier
  // both produce display names.
  const exact = choices.find(
    (choice) =>
      String(choice.name ?? "").toLowerCase() === lower ||
      String(choice.key ?? "").toLowerCase() === lower
  );
  if (exact) {
    return { status: ENUM_RESOLVED, value: exact.key, label: exact.name };
  }

  const candidates = choices
    .filter((choice) => shareAToken(choice.name, text))
    .slice(0, MAX_CANDIDATES)
    .map((choice) => ({ value: choice.key, label: choice.name }));
  if (candidates.length)
    return { status: ENUM_AMBIGUOUS, label: text, candidates };
  return { status: ENUM_UNMATCHED, label: text };
};

const resolveCurrencyField = (raw, ctx) => {
  const text = String(raw ?? "").trim();
  if (!text) return { status: ENUM_EMPTY, label: "" };

  const list = ctx?.currencyCodesList;
  if (!Array.isArray(list) || list.length === 0) {
    return { status: ENUM_UNCHECKED, value: raw, label: text };
  }

  const upper = text.toUpperCase();
  const match = list.find((code) => String(code).toUpperCase() === upper);
  if (match) return { status: ENUM_RESOLVED, value: match, label: match };
  return { status: ENUM_UNMATCHED, label: text };
};

/**
 * Resolve one enum-backed value against its option source.
 *
 * @param {string} fieldKey backend ingestion field key (e.g. "naics")
 * @param {*} raw the proposed value — AI object, web display string, or manual edit
 * @param {Object} ctx { dispatch, claimsApi, industryClassChoices, currencyCodesList }
 * @returns {Promise<{status: string, value?: *, label: string, candidates?: Array}>}
 *   `resolved` carries the canonical value to store; `ambiguous` carries candidates;
 *   `unmatched` carries nothing storable; `unchecked` means the option source was
 *   unreachable and `value` is the input, unverified.
 */
export const resolveEnumField = async (fieldKey, raw, ctx = {}) => {
  const config = ENUM_FIELDS[fieldKey];
  if (!config)
    return { status: ENUM_RESOLVED, value: raw, label: String(raw ?? "") };

  if (config.kind === "code") return resolveCodeField(config, raw, ctx);
  if (config.kind === "choice") return resolveChoiceField(raw, ctx);
  return resolveCurrencyField(raw, ctx);
};

/**
 * Resolve every enum-backed entry in a {backendKey: value} map.
 *
 * Non-enum keys pass through untouched, so callers can hand over the whole field set.
 * Values that resolve to the same thing they came in as keep their original reference.
 *
 * @returns {Promise<{values: Object, issues: Array<{fieldKey, status, label, candidates}>}>}
 *   `values` omits keys that could not be resolved; each of those appears in `issues`.
 */
export const resolveEnumFields = async (fields, ctx = {}) => {
  const values = {};
  const issues = [];

  const entries = Object.entries(fields || {});
  const results = await Promise.all(
    entries.map(async ([fieldKey, raw]) => {
      if (!isEnumBackedField(fieldKey) || raw == null) return null;
      return [fieldKey, await resolveEnumField(fieldKey, raw, ctx)];
    })
  );
  const byField = new Map(results.filter(Boolean));

  entries.forEach(([fieldKey, raw]) => {
    const result = byField.get(fieldKey);
    if (!result) {
      values[fieldKey] = raw;
      return;
    }
    if (result.status === ENUM_RESOLVED || result.status === ENUM_UNCHECKED) {
      // Reuse the incoming reference only when it is already the right *shape* and
      // says the same thing — a display string that happens to name the same option
      // still has to be replaced by the canonical value program state can store.
      const bothObjects =
        raw !== null &&
        result.value !== null &&
        typeof raw === "object" &&
        typeof result.value === "object";
      const reusable =
        raw === result.value ||
        (bothObjects && sameEnumValue(result.value, raw));
      values[fieldKey] = reusable ? raw : result.value;
      return;
    }
    if (result.status === ENUM_EMPTY) return;
    issues.push({
      fieldKey,
      status: result.status,
      label: result.label,
      candidates: result.candidates || [],
    });
  });

  return { values, issues };
};

/** One-line reviewer-facing summary of what was rejected and why. */
export const describeEnumIssues = (issues, labels = {}) => {
  if (!issues?.length) return "";
  const parts = issues.map(({ fieldKey, status, label, candidates }) => {
    const name = labels[fieldKey] || fieldKey;
    if (status === ENUM_AMBIGUOUS && candidates?.length) {
      return `${name} "${label}" is not an exact option (closest: ${candidates[0].label})`;
    }
    return `${name} "${label}" is not a valid option`;
  });
  return `${parts.join("; ")} — left blank for you to pick.`;
};
